import type { LayerRenderer } from '../registry'

/**
 * Visages (slot `face`). Ancres anatomiques VERROUILLÉES du plan Lot 1 :
 * tête ellipse (60,52) rx16 ry18.5, cou 54-66 × 64-78, yeux y≈49.6
 * (pupilles (52.9,49.6) / (66.8,49.6)), bouche y≈65.4, oreilles (44,58) /
 * (76,58), menton y≈70. Les 45 items variantes (chapeaux, coiffures,
 * barbes, accessoires) se calent sur ces ancres — ne JAMAIS les déplacer.
 *
 * Style « peint vectoriel » (maquette validée) : peau en gradient 2 stops,
 * ombre de flanc droit semi-transparente, highlight de pommette gauche,
 * yeux construits (amande + iris + éclat), sourcils en formes pleines,
 * nez modelé, bouche à 2 valeurs, ombre portée du couvre-chef sur le front.
 * Les 4 teints partagent la même construction via `paintedFace`.
 */
interface FaceTint {
  /** Gradient de peau, 2 stops (haut-gauche clair → bas-droit chaud). */
  skin: [string, string]
  /** Ombre de flanc / creux (semi-transparente sur la peau). */
  shade: string
  /** Traits marqués : paupières, narine, ombre du couvre-chef. */
  deep: string
  /** Sourcils (pleins, couleur cheveux). */
  brow: string
  /** Lumières : pommette, arête du nez. */
  light: string
  /** Lèvre inférieure. */
  lip: string
  /** Ligne de bouche + ombre sous-lèvre. */
  lipShade: string
  /** Blanc des yeux. */
  white: string
}

const TINTS: Record<string, FaceTint> = {
  face_teint_hale: {
    skin: ['#F2CFA0', '#D89A66'],
    shade: '#C68B5C',
    deep: '#5A3A22',
    brow: '#2E1B10',
    light: '#F8DDB6',
    lip: '#A55C42',
    lipShade: '#7A452A',
    white: '#F7EFDE',
  },
  face_teint_clair: {
    skin: ['#F7DEC4', '#E0B490'],
    shade: '#D9A87A',
    deep: '#6B4A2E',
    brow: '#4A3018',
    light: '#FAE8D2',
    lip: '#C77B5E',
    lipShade: '#8A4E33',
    white: '#F7EFDE',
  },
  face_teint_cuivre: {
    skin: ['#D9945C', '#A9663A'],
    shade: '#8F5630',
    deep: '#4A2E1A',
    brow: '#26160B',
    light: '#EFC08E',
    lip: '#8F4B31',
    lipShade: '#63301C',
    white: '#F7EFDE',
  },
  face_teint_ebene: {
    skin: ['#8C5A3C', '#5A3623'],
    shade: '#472817',
    deep: '#2E1B10',
    brow: '#1A0D06',
    light: '#C99872',
    lip: '#6E3B28',
    lipShade: '#3A2113',
    white: '#F2E9D8',
  },
}

const paintedFace = (t: FaceTint): LayerRenderer => ({ uid }) => (
  <>
    <defs>
      <linearGradient id={`${uid}-skin`} x1="0" y1="0" x2="0.6" y2="1">
        <stop offset="0" stopColor={t.skin[0]} />
        <stop offset="1" stopColor={t.skin[1]} />
      </linearGradient>
    </defs>
    {/* cou + ombre portée du menton sur le cou */}
    <path d="M 54 64 L 54 78 L 66 78 L 66 64 Z" fill={`url(#${uid}-skin)`} />
    <path d="M 54 64 L 66 64 L 66 72.5 C 62 74.3 58 74.3 54 72.5 Z" fill={t.shade} opacity="0.5" />
    {/* oreilles (ancres (44,58)/(76,58)) : peintes SOUS l'ellipse, seule la
        partie saillante dépasse ; le lobe reçoit la boucle des accessoires */}
    <path d="M 45.7 49.6 C 42.6 48.2 40.6 50.6 41.4 54.2 C 42.2 57.6 44.3 59.8 45.8 59.2 C 46.5 56.1 46.5 52.7 45.7 49.6 Z" fill={`url(#${uid}-skin)`} />
    <path d="M 44.5 51.6 C 43.1 51.2 42.3 52.4 42.7 54.4 C 43.1 56.1 44.1 57.3 44.8 57 C 45.1 55.2 45 53.3 44.5 51.6 Z" fill={t.shade} opacity="0.7" />
    <path d="M 74.3 49.6 C 77.4 48.2 79.4 50.6 78.6 54.2 C 77.8 57.6 75.7 59.8 74.2 59.2 C 73.5 56.1 73.5 52.7 74.3 49.6 Z" fill={`url(#${uid}-skin)`} />
    <path d="M 75.5 51.6 C 76.9 51.2 77.7 52.4 77.3 54.4 C 76.9 56.1 75.9 57.3 75.2 57 C 74.9 55.2 75 53.3 75.5 51.6 Z" fill={t.shade} opacity="0.7" />
    {/* tête (ancre : ellipse (60,52) rx16 ry18.5) */}
    <ellipse cx="60" cy="52" rx="16" ry="18.5" fill={`url(#${uid}-skin)`} />
    {/* ombre de flanc droit épousant l'ellipse */}
    <path d="M 74 46 C 74.6 52 73 60 69.5 64.5 C 66.4 68.5 63.3 70.5 60.2 70.8 C 64.9 69 68 65.5 70 59.5 C 71.8 54.4 72.7 50.4 72.4 46 Z" fill={t.shade} opacity="0.6" />
    {/* ombre portée du couvre-chef sur le front */}
    <path d="M 49 39 L 71 39 C 72.6 41.2 73 43.4 72.4 45.4 C 64.2 41.8 55.8 41.8 47.6 45.4 C 47 43.4 47.4 41.2 49 39 Z" fill={t.deep} opacity="0.2" />
    {/* highlight de pommette gauche */}
    <ellipse cx="51.6" cy="55.2" rx="3.4" ry="4.6" fill={t.light} opacity="0.4" transform="rotate(-12 51.6 55.2)" />
    {/* sourcils en formes pleines effilées */}
    <path d="M 56.7 46.4 C 54.3 44.2 50.7 44 48.5 45.4 C 50.9 45.2 53.9 45.8 55.9 47.8 Z" fill={t.brow} />
    <path d="M 62.6 46.6 C 65.2 44.4 69.2 44.4 71.6 46.2 C 69.2 45.6 65.6 46 63.4 48 Z" fill={t.brow} />
    {/* yeux construits : amande + paupière + iris + éclat (ancres y≈49.6) */}
    <path d="M 50 49.5 C 51.6 48.2 54.2 48.2 55.6 49.5 C 54.2 50.7 51.6 50.7 50 49.5 Z" fill={t.white} />
    <path d="M 49.8 49.3 C 51.5 47.8 54.2 47.8 55.7 49.2" fill="none" stroke={t.deep} strokeWidth="0.8" strokeLinecap="round" />
    <circle cx="52.9" cy="49.6" r="1.25" fill="#20303E" />
    <circle cx="52.5" cy="49.2" r="0.4" fill={t.white} />
    <path d="M 63.4 49.5 C 65.2 48.1 68.2 48.1 70 49.6 C 68.2 50.9 65.2 50.9 63.4 49.5 Z" fill={t.white} />
    <path d="M 63.2 49.3 C 65.1 47.7 68.3 47.7 70.2 49.3" fill="none" stroke={t.deep} strokeWidth="0.8" strokeLinecap="round" />
    <circle cx="66.8" cy="49.6" r="1.35" fill="#20303E" />
    <circle cx="66.4" cy="49.2" r="0.4" fill={t.white} />
    {/* nez modelé : arête en ombre pleine + narine + filet de lumière */}
    <path d="M 59.6 50.5 C 58.7 53.7 57.8 56.8 56.6 59.6 C 56 61.1 56.8 62.2 58.4 62.1 C 59.5 62 60.5 61.7 61.4 61.9 C 60.3 60.7 59.9 59.6 60 57.8 C 60.1 55.3 59.9 52.8 59.6 50.5 Z" fill={t.shade} opacity="0.75" />
    <circle cx="56.8" cy="60.9" r="0.6" fill={t.deep} opacity="0.8" />
    <path d="M 58.8 51 C 58 54 57.2 56.9 56.3 59.4" fill="none" stroke={t.light} strokeWidth="1" strokeLinecap="round" opacity="0.6" />
    {/* bouche à 2 valeurs : ligne + lèvre + ombre sous-lèvre (ancre y≈65.4) */}
    <path d="M 55.4 64.9 C 58 66.4 62 66.4 64.6 64.9" fill="none" stroke={t.lipShade} strokeWidth="1.2" strokeLinecap="round" />
    <path d="M 56.3 66.3 C 58.6 67 61.4 67 63.7 66.3 C 62.8 68.2 61.4 69.1 60 69.1 C 58.6 69.1 57.2 68.2 56.3 66.3 Z" fill={t.lip} />
    <path d="M 57.8 69.9 C 59.3 70.4 60.7 70.4 62.2 69.9" fill="none" stroke={t.lipShade} strokeWidth="1" strokeLinecap="round" opacity="0.45" />
  </>
)

export const FACES: Record<string, LayerRenderer> = Object.fromEntries(
  Object.entries(TINTS).map(([key, tint]) => [key, paintedFace(tint)]),
)
